import { ArrowBack } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import InputField from 'packages/novous_web/src/common/components/InputField';
import Button from 'packages/novous_web/src/common/components/Button';
import LiveStreamingDataTable from './components/LiveStreamingDataTable';

interface CompanyData {
    id: string;
    name: string;
    description: string;
    type: string;
    crn: string;
    address: {
        fullAddress: string;
        city: string;
        state: string;
        pincode: number;
        country: string;
    };
    contacts: {
        telephoneNo: string;
        mobileNo: string;
        email: string;
    };
    url: string;
    status: number;
    deviceTypes: string[] | null;
}

const LiveStreamingConfig = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [formData, setFormData] = useState({
        maxLiveStreamsCount: '',
        maxLiveStreamsTime: ''
    });
    const [errors, setErrors] = useState({
        maxLiveStreamsCount: '',
        maxLiveStreamsTime: ''
    });

    const {
        data: companyList,
        error: companyListError,
        loading: companyListLoading,
        fetchData: fetchCompanyListData,
    } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, "GET");

    const {
        data: companyListByCrn,
        error: companyListByCrnError,
        loading: companyListByCrnLoading,
        fetchData: fetchCompanyListByCrnData,
    } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, "POST");

    const {
        data: SuperAdminLiveStreamSetupFetchData,
        error: SuperAdminLiveStreamSetupFetchError,
        loading: SuperAdminLiveStreamSetupFetchLoading,
        fetchData: SuperAdminLiveStreamSetupFetchRequestData,
    } = useAxios<any>(API_ENDPOINTS.users.ADMIN_LIVE_STREAMING_SAVE, "POST");

    const {
        data: SuperAdminLiveGetSetupFetchData,
        error: SuperAdminLiveGetSetupFetchError,
        loading: SuperAdminLiveGetSetupFetchLoading,
        fetchData: SuperAdminLiveGetSetupFetchRequestData,
    } = useAxios<any>(API_ENDPOINTS.users.ADMIN_LIVESTREAMING_GET, "POST");

    useEffect(() => {
        if (user?.authorities === "ROLE_SUPER_ADMIN") {
            fetchCompanyListData();
        }
    }, []);

    useEffect(() => {
        if (selectedUser) {
            SuperAdminLiveGetSetupFetchRequestData({
                fleetId: selectedUser?.id
            })
        }
    }, [selectedUser])

    const handleCompanyChange = (value: string) => {
        const company = companyList?.data?.find((c: CompanyData) => c.name === value);
        setSelectedCompany(company || null);
        setSelectedUser(null);

        if (company) {
            fetchCompanyListByCrnData({
                userId: company.id,
                userCrn: company.crn
            });
        }
    };

    const handleUserChange = (value: string) => {
        const userSelected = companyListByCrn?.data?.find((u: any) => u.fullName === value);
        setSelectedUser(userSelected || null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        let hasError = false;
        const newErrors = {
            maxLiveStreamsCount: '',
            maxLiveStreamsTime: ''
        };

        if (!formData.maxLiveStreamsCount || isNaN(Number(formData.maxLiveStreamsCount)) || Number(formData.maxLiveStreamsCount) <= 0) {
            newErrors.maxLiveStreamsCount = 'Please enter a valid positive number';
            hasError = true;
        }

        if (!formData.maxLiveStreamsTime || isNaN(Number(formData.maxLiveStreamsTime)) || Number(formData.maxLiveStreamsTime) <= 0) {
            newErrors.maxLiveStreamsTime = 'Please enter a valid positive number';
            hasError = true;
        }

        setErrors(newErrors);

        if (hasError || !selectedUser || !selectedCompany) return;

        const payload = {
            fleetId: selectedUser.id,
            maxLiveStreamViewers: Number(formData.maxLiveStreamsCount),
            maxViewDurationMillis: Number(formData.maxLiveStreamsTime),
        };

        SuperAdminLiveStreamSetupFetchRequestData(payload);
        setFormData({
            maxLiveStreamsCount: '',
            maxLiveStreamsTime: ''
        })
        if (selectedUser) {
            SuperAdminLiveGetSetupFetchRequestData({
                fleetId: selectedUser?.id
            })
        }
    };


    return (
        <>
            <Box sx={{ width: "80%", display: "flex", gap: "16px", flexDirection: "column" }}>
                <div className="w-full">
                    <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
                        <ArrowBack className="mr-1" /> Back
                    </button>
                </div>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", marginTop: "32px", marginBottom: "32px", border: "1px solid #D9D9D9", padding: "16px", borderRadius: "24px" }}>
                    <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>Live Streaming Configration</Typography>
                    <Box className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                        <CustomDropDownInput
                            label="Company"
                            options={companyList?.data?.map((company: CompanyData) => ({
                                value: company.name,
                                label: company.name
                            })) || []}
                            value={selectedCompany?.name || ''}
                            onChange={handleCompanyChange}
                            placeholder="Select Company"
                            showDropdownArrow={true}
                        />

                        <CustomDropDownInput
                            label={selectedCompany ? "User" : "Select company first"}
                            options={companyListByCrn?.data?.map((user: any) => ({
                                value: user.fullName,
                                label: user.fullName
                            })) || []}
                            value={selectedUser?.fullName || ''}
                            onChange={handleUserChange}
                            placeholder={selectedCompany ? "Select User" : "Select company first"}
                            isDisabled={!selectedCompany}
                            showDropdownArrow={true}
                        />
                    </Box>
                    {selectedUser &&
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px", marginTop: "32px" }}>
                            <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>Streaming Configration Form</Typography>
                            <form onSubmit={handleSubmit} className="w-full flex flex-col gap-8">
                                <Box sx={{ width: "100%", display: "flex", gap: "16px" }}>
                                    <Box sx={{ display: "flex", gap: "8px", width: "100%" }}>
                                        <InputField
                                            label="Max Live Streams Count"
                                            name="maxLiveStreamsCount"
                                            value={formData.maxLiveStreamsCount}
                                            onChange={(e) => {
                                                const { value } = e.target;
                                                setFormData((prev) => ({ ...prev, maxLiveStreamsCount: value }));
                                                if (errors.maxLiveStreamsCount) {
                                                    setErrors((prev) => ({ ...prev, maxLiveStreamsCount: '' }));
                                                }
                                            }}
                                            status={errors.maxLiveStreamsCount ? "error" : undefined}
                                            errorMessage={errors.maxLiveStreamsCount}
                                            placeholder="Enter Max Live Streams Count"
                                        />
                                        <InputField
                                            label="Max Live Streams Time (Seconds)"
                                            name="maxLiveStreamsTime"
                                            value={formData.maxLiveStreamsTime}
                                            onChange={(e) => {
                                                const { value } = e.target;
                                                setFormData((prev) => ({ ...prev, maxLiveStreamsTime: value }));
                                                if (errors.maxLiveStreamsTime) {
                                                    setErrors((prev) => ({ ...prev, maxLiveStreamsTime: '' }));
                                                }
                                            }}
                                            status={errors.maxLiveStreamsTime ? "error" : undefined}
                                            errorMessage={errors.maxLiveStreamsTime}
                                            placeholder="Enter Max Live Streams Time in Seconds"
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ width: "50%", display: "flex", gap: "16px", justifyContent: "flex-end", alignSelf: "flex-end", alignItems: "center" }}>
                                    <Button
                                        text="Cancel"
                                        variant='outlined'
                                        onClick={() => console.log('cancel')}
                                    />
                                    <Button
                                        text="Save"
                                        variant='contained'
                                        onClick={() => console.log('save')}
                                    />
                                </Box>
                            </form>
                        </Box>
                    }
                </Box>
            </Box>
            {selectedUser &&
                <LiveStreamingDataTable SuperAdminLiveGetSetupFetchData = {SuperAdminLiveGetSetupFetchData} />
            }
        </>
    )
}

export default LiveStreamingConfig